import {
  ATTRIBUTE_NAMES,
  MAX_CHARACTER_LEVEL,
  MAX_SKILL_POINTS,
  SKILLS,
  type CharacterClass,
  type CharacterProgress,
  type PointLedger,
  type Skill,
  type SkillEffect,
  type SkillLockReason,
  classSkillIds,
  classSkills,
  createLedger,
  experienceForLevel,
  experienceToNextLevel,
  skillById,
  skillLevel,
  skillLockReason,
  skillManaCost,
  spendPoints,
  treeSkills,
  treesOfClass,
} from '@d2/core';

import { type CharacterFacts } from '../game/character-feed';
import { type HudProgress, type HudVitals } from './hud';
import { type SkillTreeView, type TreeSkillView } from './skill-tree-model';

/**
 * What the two panels are given, composed **out of `@d2/core`**.
 *
 * This is the module the whole milestone turns on, so it is worth being blunt
 * about what it may and may not do. `TreeSkillView` wants `allocatable` and
 * `lockedReason` already decided, and neither is on the wire — deliberately.
 * `@d2/schema`'s `ProgressionPayload` says why: they are
 * `skillLockReason(skill, progress)`, *"and every input to it is either here
 * (the allocation) or on `PlayerState` (the level). Sending the answer would be
 * sending a rule's output thirty times per seat… Running core on the client is
 * not restating a rule; it is calling the one implementation of it, as
 * `findPath` and `generateDungeon` already are."*
 *
 * So every gate below is a **call**, never a comparison:
 *
 *   - the row's character level and the prerequisite walk are `skillLockReason`;
 *   - a skill's grid position, its name, its cap and its required level are rows
 *     of `SKILLS`, read through `treeSkills` and `skillById`;
 *   - what is standing on a skill is `skillLevel`, off a ledger built with
 *     `createLedger` and `spendPoints`;
 *   - the experience bar's two numbers are `experienceForLevel` and
 *     `experienceToNextLevel`, against D2's own 99-row table.
 *
 * There is no `if (level < requiredLevel)` in this file and there must never be
 * one. A level gate written here as well as in core is the same rule in two
 * places, and the second copy is the one that goes stale.
 *
 * What *is* this module's is presentation: which words a lock reason is put into,
 * what a cell's one-line description says, and which of a class's skills a
 * hotkey can be pointed at.
 *
 * ## The character is rebuilt from two channels, and neither is enough alone
 *
 * `PlayerState` carries the level and the lifetime experience; the addressed
 * `SERVER_MESSAGE.progression` carries the ledger. `progressOf` below puts them
 * back together into the `CharacterProgress` core's own functions take. It is
 * assembled rather than constructed through `createCharacterProgress` on
 * purpose: that constructor **throws** when the level and the experience
 * disagree, which is right inside the simulation and wrong here, where the two
 * halves arrive on different channels at different moments and a client holding
 * one snapshot ahead of the other for a frame is ordinary rather than corrupt.
 */

/** The HUD's two halves, ready to render. */
export interface HudView {
  readonly vitals: HudVitals;
  readonly progress: HudProgress;
}

/** A cast a hotkey asks for. The whole of `UseSkillPayload` bar its sequence. */
export interface CastRequest {
  readonly skill: string;
  /** The body it lands on, or `null` for a self-cast — written out, never absent. */
  readonly target: string | null;
}

/**
 * A level this client can look up in core's table.
 *
 * `PlayerState.level` starts at **zero**, and a room only writes it for a seat
 * whose progression it actually loaded. Zero is therefore not a level: it is
 * *"this room has said nothing about this character"*, and `experienceForLevel`
 * would throw on it rather than answer. The upper end is the wire's `uint8`
 * against core's own ceiling of 99.
 */
const isDescribedLevel = (level: number): boolean =>
  Number.isInteger(level) && level >= 1 && level <= MAX_CHARACTER_LEVEL;

/** Points the server says are standing on a skill, clamped to a row core will read. */
const readableLevel = (points: number): number =>
  Math.min(Math.max(1, Math.floor(points)), MAX_SKILL_POINTS);

/**
 * The class's skill ledger, rebuilt from the allocations the server reported.
 *
 * Opened over `spent + unspent`, because `granted` is a **lifetime** total and
 * the wire sends what is *left*: a ledger opened over the unspent alone would
 * report `pointsAvailable` of zero the moment anything had been spent, and the
 * panel would refuse every allocation the character had actually earned.
 *
 * An allocation core's ledger refuses — a skill this class does not hold, a
 * fractional count — is skipped rather than thrown over. That is a client and a
 * server disagreeing about the catalogue, which is a deployment skew and not a
 * game state; a blank screen is a worse answer than a tree with one cell out of
 * date.
 */
const skillLedgerOf = (
  classId: CharacterClass,
  facts: CharacterFacts,
  catalogue: readonly Skill[],
): PointLedger<string> => {
  const allocations = facts.ledger?.skills ?? [];
  const spent = allocations.reduce((total, allocation) => total + Math.max(0, allocation.points), 0);

  let ledger = createLedger(
    classSkillIds(classId, catalogue),
    spent + Math.max(0, facts.ledger?.skillPointsUnspent ?? 0),
  );

  for (const allocation of allocations) {
    const put = spendPoints(ledger, allocation.skillId, allocation.points);

    if (put.ok) ledger = put.ledger;
  }

  return ledger;
};

/**
 * The character as core's own functions want it.
 *
 * `attributePoints` is opened but never read: `skillLockReason` and `skillLevel`
 * take a whole `CharacterProgress` and touch only the level and the skill
 * ledger, and this client is not told the attribute allocation at all — the
 * panels draw the unspent total and nothing draws where the spent ones went.
 * Building it empty is the honest shape of "not on this wire", and nothing here
 * derives an attribute from it.
 */
const progressOf = (
  classId: CharacterClass,
  facts: CharacterFacts,
  catalogue: readonly Skill[],
): CharacterProgress => {
  const level = facts.vitals?.level ?? 0;

  return {
    class: classId,
    level,
    experience: facts.vitals?.experience ?? 0,
    attributePoints: createLedger(ATTRIBUTE_NAMES, Math.max(0, facts.ledger?.statPointsUnspent ?? 0)),
    skillPoints: skillLedgerOf(classId, facts, catalogue),
  };
};

/** What a skill does, in the fewest words that are still true. Presentation, from core's own row. */
const effectWords = (effect: SkillEffect): string => {
  switch (effect.kind) {
    case 'spell-damage':
      return `${effect.damageType} damage`;
    case 'damage-over-time':
      return `${effect.damageType} damage over time`;
    case 'weapon-attack':
      return 'a weapon attack';
    case 'timed-modifier':
      return 'a timed effect';
    case 'passive':
      return 'a passive bonus';
  }
};

const sentence = (words: string): string => `${words.charAt(0).toUpperCase()}${words.slice(1)}`;

/**
 * One line under a cell's name.
 *
 * The mana is `skillManaCost`'s, read at the level this character has bought —
 * or at one point, for a skill they have not bought yet, which is what a D2
 * tooltip shows and is the only level a table has to answer with.
 */
const describeSkill = (skill: Skill, points: number): string => {
  const words = sentence(effectWords(skill.effect));

  if (skill.target === 'none') return `${words}. Never cast — it works while a point stands on it.`;

  const cost = skillManaCost(skill, readableLevel(points));

  return cost > 0 ? `${words}. ${cost} mana.` : `${words}.`;
};

/** Core's lock, in words a player can act on. The reason is core's; the sentence is this file's. */
const lockWords = (reason: SkillLockReason, skill: Skill, catalogue: readonly Skill[]): string => {
  if (reason === 'level') return `Requires character level ${skill.requiredLevel}.`;

  const names = skill.prerequisites.map((id) => skillById(id, catalogue)?.name ?? id);

  return `Requires a point in ${names.join(' and ')}.`;
};

const cellOf = (skill: Skill, progress: CharacterProgress, catalogue: readonly Skill[]): TreeSkillView => {
  const points = skillLevel(progress, skill.id);
  // Asked once, so `allocatable` and `lockedReason` cannot disagree — the same
  // discipline `skill-tree-model.ts` keeps about its own two blocks.
  const lock = skillLockReason(skill, progress);

  return {
    id: skill.id,
    name: skill.name,
    description: describeSkill(skill, points),
    tier: skill.row,
    column: skill.column,
    points,
    maximumPoints: MAX_SKILL_POINTS,
    requiredLevel: skill.requiredLevel,
    prerequisites: skill.prerequisites,
    allocatable: lock === null,
    lockedReason: lock === null ? null : lockWords(lock, skill, catalogue),
  };
};

/**
 * The HUD's numbers, or `null` when there is nothing honest to draw.
 *
 * `null` rather than zeroes, because the panel has no way to say "unknown": an
 * experience bar drawn from a level nobody has stated would read as *you have
 * earned nothing*, and an orb drawn from an undescribed pool would read as a
 * character about to die.
 */
export const hudViewOf = (facts: CharacterFacts): HudView | null => {
  const vitals = facts.vitals;
  if (vitals === null || !isDescribedLevel(vitals.level)) return null;

  return {
    vitals: {
      life: vitals.life,
      maximumLife: vitals.maximumLife,
      mana: vitals.mana,
      maximumMana: vitals.maximumMana,
    },
    progress: {
      level: vitals.level,
      // The level's own floor, subtracted here so the panel never has to know
      // what a floor is. Core's table on both sides of the subtraction.
      experienceIntoLevel: vitals.experience - experienceForLevel(vitals.level),
      experienceForNextLevel: experienceToNextLevel(vitals.level),
      unspentSkillPoints: unspentSkillPointsOf(facts),
      unspentStatPoints: Math.max(0, facts.ledger?.statPointsUnspent ?? 0),
    },
  };
};

/**
 * Skill points the server says are left, or nothing at all before it has said.
 *
 * Never derived. `granted` is a lifetime total that a Token of Absolution moves
 * points back into without touching a level, so no arithmetic over the level
 * recovers it — which is exactly why it is on the wire (`@d2/schema`).
 */
export const unspentSkillPointsOf = (facts: CharacterFacts): number =>
  Math.max(0, facts.ledger?.skillPointsUnspent ?? 0);

/**
 * The class's three tabs, every gate resolved by core, ready for the panel.
 *
 * `catalogue` defaults to the shipped table and is overridable for the reason
 * every lookup in core's own `skills.ts` is: *"a fixture tree passes its own"*.
 * It is what lets a suite exercise a prerequisite lock, which the nine authored
 * skills cannot express — D2 has no cross-tab prerequisite and the one
 * in-tab prerequisite this slice has is `deferredPrerequisites` (`skills.ts`,
 * `TODO.md` #470) — and it is the whole of what changes here when the other
 * eighty-one land.
 */
export const skillTreesOf = (
  classId: CharacterClass,
  facts: CharacterFacts,
  catalogue: readonly Skill[] = SKILLS,
): readonly SkillTreeView[] => {
  const progress = progressOf(classId, facts, catalogue);

  return treesOfClass(classId).map((tree) => ({
    id: tree.id,
    name: tree.label,
    skills: treeSkills(tree.id, catalogue).map((skill) => cellOf(skill, progress, catalogue)),
  }));
};

/**
 * The skills a hotkey can be pointed at, in core's table order.
 *
 * A passive is left out because core says so: `SkillTarget` of `'none'` is
 * *"D2 passives are never cast at all"*, so a slot holding one would be a key
 * that reliably does nothing.
 */
export const castableSkills = (
  classId: CharacterClass,
  catalogue: readonly Skill[] = SKILLS,
): readonly Skill[] => classSkills(classId, catalogue).filter((skill) => skill.target !== 'none');

/**
 * What a slot's key asks the server for, or `null` when it asks for nothing.
 *
 * The one decision here is *whether there is anything to send*, and it is not a
 * game rule: a cast at nobody would be answered `no-target` by `admitCast`, so
 * declining to send it costs the player nothing and costs the room an allowance
 * it would otherwise have spent. Everything a cast can be refused **for** — the
 * points, the row, the pool, the cooldown, the reach, the sight, the target's
 * life and side — is the server's, from core, and nothing here anticipates any
 * of it.
 */
export const castRequestOf = (skill: Skill, targetId: string | null): CastRequest | null => {
  if (skill.target === 'none') return null;
  if (skill.target === 'self') return { skill: skill.id, target: null };

  return targetId === null ? null : { skill: skill.id, target: targetId };
};
